/**
 * apps/command/src/screens/OperatorConsole.tsx — §15.7's working console.
 *
 * "Operator view — the working console. Fleet grid, live alert feed, command
 * palette, everything dense and keyboard-driven."
 *
 * ============================================================================
 * REBUILT AS A DENSITY SCREEN (2026-08-18). WHAT CHANGED AND WHY.
 * ============================================================================
 *
 * It used to be a status strip over a grid of tiles — three kiosks rendered as
 * three large cards, which is a layout that looks the same whether the province
 * has three machines or thirty-five and answers no question either way. An
 * operator's console has to answer, in one screen: what is broken, where is
 * work piling up, what has just changed, and what is every machine doing right
 * now. That is a TABLE plus a stream, not a wall of cards.
 *
 * So: the pulse strip, eight counters, a per-kiosk table with the readings that
 * actually predict failure (disk, queue, response time, version), the live
 * alert feed beside it, and a status pipeline showing where the fleet sits.
 *
 * It remains a DIFFERENT ROUTE from the governor's home rather than the same
 * screen with a role switch inside it. §15.7's whole point is that the
 * application opens differently per person, and one component branching on role
 * is how "the governor must not land in a cockpit" quietly becomes "the
 * governor lands in a cockpit with three things hidden".
 *
 * The command palette is P6 and is genuinely absent — named here rather than
 * mimed on screen, because a disabled control an operator cannot use is an
 * invitation to ask why, and the answer is never interesting.
 */
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { KIOSK_STATUSES, type KioskStatus } from '@navoiy/domain';
import { useSession } from '../auth/SessionContext.js';
import { t } from '../i18n/index.js';
import { ageLabel, integer, percent } from '../lib/format.js';
import { statusOf, useFleet, type MbtKiosk } from '../realtime/useFleet.js';
import { Notice, Panel, PanelHeader, Skeleton, StatusPill } from '../ui/index.jsx';
import { AlertFeed } from '../components/AlertFeed.jsx';
import { KpiCard, PageHeader, PipelineStage, SystemPulse, TabStrip } from '../ui/console.jsx';
import { BarChart, Gauge, StatusDonut } from '../ui/charts.jsx';
import {
  DataTable,
  LiveBadge,
  ProgressBar,
  toneClass,
  type Column,
  type Tone,
} from '../ui/data.jsx';

const TONE_OF: Record<string, Tone> = {
  healthy: 'ok',
  warning: 'warn',
  critical: 'crit',
  offline: 'offline',
  maintenance: 'info',
};
const toneOf = (status: string): Tone => TONE_OF[status] ?? 'neutral';


type Lens = 'all' | 'attention' | 'offline';

export function OperatorConsole(): React.JSX.Element {
  const { expire } = useSession();
  const onExpired = useCallback(() => expire(), [expire]);
  const fleet = useFleet(onExpired);
  const [lens, setLens] = useState<Lens>('all');

  if (fleet.error === 'forbidden') return <Notice tone="crit">{t('error.forbidden')}</Notice>;

  const byStatus = new Map<KioskStatus, number>(KIOSK_STATUSES.map((status) => [status, 0]));
  for (const kiosk of fleet.kiosks) {
    const status = statusOf(kiosk, fleet.now);
    byStatus.set(status, (byStatus.get(status) ?? 0) + 1);
  }

  const queueTotal = fleet.kiosks.reduce(
    (sum, kiosk) => sum + (kiosk.telemetry?.work?.queue_depth ?? 0),
    0,
  );
  const appealsTotal = fleet.kiosks.reduce(
    (sum, kiosk) => sum + (kiosk.telemetry?.work?.appeals_today ?? 0),
    0,
  );
  const alertsTotal = fleet.kiosks.reduce((sum, kiosk) => sum + (kiosk.open_alerts ?? 0), 0);
  /**
   * Worst disk in the fleet, not the average.
   *
   * An average across thirty-five machines hides the one that is about to stop
   * accepting appeals, which is the only reading on this row anybody can act
   * on. The same argument as "worst kiosk wins its mahalla's row" on the
   * executive home.
   */
  const worstDisk = fleet.kiosks.reduce<number | null>((worst, kiosk) => {
    const free = kiosk.telemetry?.health?.disk_free_pct;
    if (typeof free !== 'number') return worst;
    return worst === null || free < worst ? free : worst;
  }, null);
  const slowest = fleet.kiosks.reduce<number | null>((worst, kiosk) => {
    const rtt = kiosk.telemetry?.network?.rtt_ms;
    if (typeof rtt !== 'number') return worst;
    return worst === null || rtt > worst ? rtt : worst;
  }, null);
  const versions = new Set(
    fleet.kiosks.map((kiosk) => kiosk.app_version).filter((v): v is string => Boolean(v)),
  );

  const needsAttention = (kiosk: MbtKiosk): boolean => {
    const status = statusOf(kiosk, fleet.now);
    return status === 'critical' || status === 'warning' || status === 'offline';
  };

  const rows = fleet.kiosks
    .filter((kiosk) => {
      if (lens === 'offline') return statusOf(kiosk, fleet.now) === 'offline';
      if (lens === 'attention') return needsAttention(kiosk);
      return true;
    })
    /* Exception-first, always. An operator opens this screen because something
       is wrong; sorting alphabetically makes them hunt for it. */
    .sort((a, b) => {
      const rank = (k: MbtKiosk): number => {
        const status = statusOf(k, fleet.now);
        return status === 'critical' ? 0 : status === 'offline' ? 1 : status === 'warning' ? 2 : 3;
      };
      return rank(a) - rank(b) || a.short_code.localeCompare(b.short_code);
    });

  const columns: Column<MbtKiosk>[] = [
    {
      key: 'kiosk',
      label: t('console.colKiosk'),
      render: (kiosk) => (
        <Link to={`/fleet/${kiosk.id}`} className="block min-w-0">
          <p className="truncate font-semibold text-ink-100">{kiosk.short_code}</p>
          <p className="truncate text-micro text-ink-500">{kiosk.mahalla_name}</p>
        </Link>
      ),
    },
    {
      key: 'status',
      label: t('console.colStatus'),
      render: (kiosk) => {
        const status = statusOf(kiosk, fleet.now);
        return <StatusPill status={status} label={t(`status.${status}`)} />;
      },
    },
    {
      key: 'alerts',
      label: t('console.colAlerts'),
      numeric: true,
      render: (kiosk) =>
        (kiosk.open_alerts ?? 0) === 0 ? (
          <span className="text-ink-500">0</span>
        ) : (
          <span className={(kiosk.open_alerts_critical ?? 0) > 0 ? 'font-semibold text-crit' : 'text-warn'}>
            {kiosk.open_alerts}
          </span>
        ),
    },
    {
      key: 'queue',
      label: t('console.colQueue'),
      numeric: true,
      render: (kiosk) => {
        const depth = kiosk.telemetry?.work?.queue_depth;
        if (typeof depth !== 'number') return <span className="text-ink-500">—</span>;
        return (
          <span className={depth > 0 ? 'font-semibold text-warn' : 'text-ink-300'}>{depth}</span>
        );
      },
    },
    {
      key: 'appeals',
      label: t('console.colAppeals'),
      numeric: true,
      render: (kiosk) => {
        const today = kiosk.telemetry?.work?.appeals_today;
        return typeof today === 'number' ? (
          <span className="text-ok">{today}</span>
        ) : (
          <span className="text-ink-500">—</span>
        );
      },
    },
    {
      key: 'disk',
      label: t('fleet.metric.disk'),
      render: (kiosk) => {
        const free = kiosk.telemetry?.health?.disk_free_pct;
        if (typeof free !== 'number') return <span className="text-ink-500">—</span>;
        return (
          <div className="flex items-center gap-2">
            {/* The bar shows USED, the number shows FREE — because the alert
                rule is about free space and an operator reading this row is
                checking it against that rule. */}
            <ProgressBar
              pct={100 - free}
              tone={free < 10 ? 'crit' : free < 20 ? 'warn' : 'ok'}
            />
            <span className="w-10 shrink-0 text-right tabular-nums text-ink-300">
              {Math.round(free)}%
            </span>
          </div>
        );
      },
      width: '10rem',
    },
    {
      key: 'rtt',
      label: t('fleet.metric.rtt'),
      numeric: true,
      render: (kiosk) => {
        const rtt = kiosk.telemetry?.network?.rtt_ms;
        if (typeof rtt !== 'number') return <span className="text-ink-500">—</span>;
        return <span className={rtt > 400 ? 'text-warn' : 'text-ink-300'}>{rtt} ms</span>;
      },
    },
    {
      key: 'version',
      label: t('fleet.metric.version'),
      render: (kiosk) =>
        kiosk.app_version ? (
          <span className="tabular-nums text-ink-300">{kiosk.app_version}</span>
        ) : (
          <span className="text-ink-500">—</span>
        ),
    },
    {
      key: 'seen',
      label: t('console.colSeen'),
      numeric: true,
      /* The "as of" travels with every reading in this row (§3.5). A grid of
         numbers with no age is a grid that looks live whatever its age. */
      render: (kiosk) =>
        kiosk.telemetry_at ? (
          <span className="text-ink-500">
            {ageLabel(new Date(kiosk.telemetry_at), fleet.now, t)}
          </span>
        ) : (
          <span className="text-ink-500">{t('app.never')}</span>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow={
          <>
            {fleet.live ? <LiveBadge label={t('status.live')} /> : null}
            <span>{t('console.eyebrow')}</span>
          </>
        }
        title={t('nav.console')}
        subtitle={t('console.subtitle', { n: String(fleet.kiosks.length) })}
        right={
          fleet.fetchedAt === null ? undefined : (
            <span className="text-micro tabular-nums text-ink-500">
              {t('app.asOf', { at: ageLabel(fleet.fetchedAt, fleet.now, t) })}
            </span>
          )
        }
      />

      {fleet.error === 'network' ? <Notice tone="warn">{t('error.network')}</Notice> : null}
      {fleet.scoped ? <Notice tone="quiet">{t('fleet.scopedNote')}</Notice> : null}

      <SystemPulse
        label={fleet.error === null ? t('pulse.normal') : t('pulse.degraded')}
        ok={fleet.error === null}
        chips={[
          {
            key: 'socket',
            label: fleet.live ? t('pulse.socketLive') : t('pulse.socketPoll'),
            tone: fleet.live ? 'ok' : 'warn',
          },
          {
            key: 'versions',
            /* More than one version in the fleet is a fact worth a chip: it is
               the difference between "the fix is deployed" and "the fix is
               deployed to some of them". */
            label: t('console.versions', { n: String(versions.size) }),
            tone: versions.size > 1 ? 'warn' : 'ok',
          },
          {
            key: 'queue',
            label: t('console.queueChip', { n: String(queueTotal) }),
            tone: queueTotal > 0 ? 'warn' : 'ok',
          },
        ]}
        spread={fleet.kiosks
          .map((kiosk) => kiosk.telemetry?.network?.rtt_ms)
          .filter((rtt): rtt is number => typeof rtt === 'number')}
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('status.healthy')}
          value={String(byStatus.get('healthy') ?? 0)}
          absent={t('app.notYet')}
          tone="ok"
          footerLabel={t('console.ofTotal')}
          footerValue={String(fleet.kiosks.length)}
        />
        <KpiCard
          label={t('overview.openAlerts')}
          value={String(alertsTotal)}
          absent={t('app.notYet')}
          tone={alertsTotal > 0 ? 'crit' : 'ok'}
          footerLabel={t('status.critical')}
          footerValue={String(byStatus.get('critical') ?? 0)}
        />
        <KpiCard
          label={t('overview.queueTotal')}
          value={String(queueTotal)}
          absent={t('app.notYet')}
          /* The per-kiosk queue depths as the card's own series: it shows the
             SPREAD, which the total hides. One kiosk holding twelve is a very
             different morning from twelve holding one each. */
          spread={fleet.kiosks.map((kiosk) => kiosk.telemetry?.work?.queue_depth ?? 0)}
          tone={queueTotal > 0 ? 'warn' : 'ok'}
          footerLabel={t('console.colAppeals')}
          footerValue={String(appealsTotal)}
        />
        <KpiCard
          label={t('console.worstDisk')}
          spread={fleet.kiosks
            .map((kiosk) => kiosk.telemetry?.health?.disk_free_pct)
            .filter((v): v is number => typeof v === 'number')}
          value={worstDisk === null ? null : percent(worstDisk)}
          absent={t('app.notYet')}
          tone={worstDisk === null ? 'neutral' : worstDisk < 10 ? 'crit' : worstDisk < 20 ? 'warn' : 'ok'}
          footerLabel={t('console.slowest')}
          footerValue={slowest === null ? t('app.notYet') : `${slowest} ms`}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        {/*
          THE RING — how the fleet is split, right now. Beside the pipeline
          rather than instead of it: the ring answers «what is the mix», the
          pipeline answers «how big is each part», and an operator asks both.
        */}
        <Panel>
          <PanelHeader title={t('console.split')} />
          <div className="px-4 py-4">
            <StatusDonut
              centreValue={String(byStatus.get('healthy') ?? 0)}
              centreLabel={t('status.healthy')}
              slices={KIOSK_STATUSES.filter((status) => (byStatus.get(status) ?? 0) > 0).map(
                (status) => ({
                  key: status,
                  label: t(`status.${status}`),
                  value: byStatus.get(status) ?? 0,
                  className: toneClass(toneOf(status)),
                }),
              )}
            />
          </div>
        </Panel>

        {/*
          THREE GAUGES with their targets drawn. Uptime against 100, disk
          against the 20% the alert rule fires at, and response time inverted
          against a one-second ceiling — each is a line somebody is held to, and
          a ring without its mark would be decoration.
        */}
        <Panel>
          <PanelHeader title={t('console.health')} />
          <div className="grid grid-cols-3 gap-2 px-4 py-4">
            <Gauge
              pct={
                fleet.kiosks.length === 0
                  ? null
                  : ((fleet.kiosks.length - (byStatus.get('offline') ?? 0)) /
                      fleet.kiosks.length) *
                    100
              }
              target={100}
              label={t('gauge.fleet')}
              tone={(byStatus.get('offline') ?? 0) === 0 ? 'text-ok' : 'text-warn'}
            />
            <Gauge
              pct={worstDisk}
              target={20}
              label={t('fleet.metric.disk')}
              tone={worstDisk !== null && worstDisk < 20 ? 'text-crit' : 'text-ok'}
            />
            <Gauge
              /* Inverted: a gauge that filled as latency ROSE would paint the
                 worst state as the fullest ring. 1000 ms is the ceiling. */
              pct={slowest === null ? null : Math.max(0, 100 - Math.min(100, slowest / 10))}
              target={60}
              label={t('fleet.metric.rtt')}
              tone={slowest !== null && slowest > 400 ? 'text-warn' : 'text-ok'}
            />
          </div>
        </Panel>

        {/*
          THE STATUS PIPELINE. Proportional, so «where is the fleet» is a glance
          rather than five numbers to compare — and each stage carries its count,
          because the height is for the glance and the number is for the
          decision.
        */}
        <Panel>
          <PanelHeader title={t('console.pipeline')} aside={t('console.pipelineHint')} />
          <div className="flex h-32 items-end gap-3 px-4 pb-2 pt-4">
            {KIOSK_STATUSES.map((status) => (
              <PipelineStage
                key={status}
                label={t(`status.${status}`)}
                count={byStatus.get(status) ?? 0}
                total={fleet.kiosks.length}
                tone={toneOf(status)}
              />
            ))}
          </div>
        </Panel>
      </div>

      {/*
        APPEALS TAKEN PER KIOSK TODAY. The one chart that answers «is any of
        this actually being used», which no status colour can: a fleet can be
        entirely green and serving nobody.
      */}
      <Panel>
        <PanelHeader title={t('console.perKiosk')} aside={t('console.perKioskHint')} />
        <div className="px-4 py-4">
          <BarChart
            bars={fleet.kiosks.map((kiosk) => ({
              key: kiosk.id,
              label: kiosk.short_code,
              value: kiosk.telemetry?.work?.appeals_today ?? 0,
              highlight: (kiosk.telemetry?.work?.queue_depth ?? 0) > 0,
            }))}
          />
        </div>
      </Panel>

      <div className="grid gap-4 xl:grid-cols-[3fr_2fr]">
        <Panel>
          <PanelHeader
            title={t('nav.fleet')}
            aside={
              <TabStrip
                value={lens}
                onChange={setLens}
                tabs={[
                  { value: 'all', label: t('fleet.filterAll'), count: fleet.kiosks.length },
                  {
                    value: 'attention',
                    label: t('console.needsAttention'),
                    count: fleet.kiosks.filter(needsAttention).length,
                  },
                  {
                    value: 'offline',
                    label: t('status.offline'),
                    count: byStatus.get('offline') ?? 0,
                  },
                ]}
              />
            }
          />
          {fleet.loading ? (
            <div className="px-4 py-4">
              <Skeleton className="h-64" />
            </div>
          ) : rows.length === 0 ? (
            <p className="px-4 py-8 text-center text-label text-ok">{t('console.noneInLens')}</p>
          ) : (
            <DataTable
              columns={columns}
              rows={rows}
              keyOf={(kiosk) => kiosk.id}
              total={[
                t('overview.totalRow'),
                '',
                integer(alertsTotal),
                integer(queueTotal),
                integer(appealsTotal),
                '',
                '',
                '',
                '',
              ]}
            />
          )}
        </Panel>

        <AlertFeed onExpired={onExpired} />
      </div>
    </div>
  );
}
